/**
 * 统一HTTP客户端
 * 整合请求处理、拦截器和API工厂功能
 * 
 * @author AI Assistant
 * @since 2025-01-12
 * @version 2.0.0
 */

import axios, { AxiosRequestConfig, AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { ElMessage } from 'element-plus';
import { useUserStore } from '@/store';

import { enableLog, formatPrefix, generateBrowserFingerprint} from '@/utils/app-runtime';
import { buildAppError, inferErrorCode, ErrorCode } from '@/utils/notify';

// ============= 类型定义 =============

export interface ApiCallConfig extends AxiosRequestConfig {
  // 缓存配置
  cache?: {
    enabled: boolean;
    key?: string;
    ttl?: number; // 缓存时间（毫秒）
  };

  // 重试配置
  retry?: {
    enabled: boolean;
    maxAttempts?: number;
    delay?: number;
    backoff?: 'linear' | 'exponential';
  };

  // 错误处理配置
  errorHandling?: {
    showUserError?: boolean;
    logError?: boolean;
    customHandler?: (error: any) => void;
  };

  // 加载状态配置
  loading?: {
    enabled: boolean;
    key?: string;
    showGlobalLoading?: boolean;
  };

  // 数据转换配置
  transform?: {
    response?: (data: any) => any;
    request?: (data: any) => any;
    // 去重配置
    disableDedupe?: boolean; // 禁用请求去重（用于文件上传等场景）
  };
}

export interface InterceptorConfig {
  // 请求拦截器配置
  addAuth?: boolean;           // 是否添加认证信息
  addFingerprint?: boolean;    // 是否添加浏览器指纹
  addTimestamp?: boolean;      // 是否添加时间戳
  baseUrlHandler?: (config: AxiosRequestConfig) => string; // 自定义 baseURL 处理
  
  // 响应拦截器配置
  enableErrorHandler?: boolean; // 是否启用错误处理
  skipAuthRequests?: boolean;   // 是否跳过认证请求的错误提示
  standardResponseFormat?: boolean; // 是否处理标准响应格式 {code, message, data}
  
  // 错误处理配置
  errorConfig?: {
    showUserError?: boolean;
    logConsoleError?: boolean;
    customErrorHandler?: (error: any) => void;
  };
}

// ============= 错误管理器 =============

class ErrorManager {
  private static instance: ErrorManager;
  private errorCache = new Map<string, number>();
  private consoleErrorCache = new Map<string, number>();
  private readonly MODULE_PREFIX = formatPrefix('ErrorManager');

  // 错误类型配置
  private readonly ERROR_CONFIG = {
    network: { duration: 8000, showUser: true, logConsole: true },
    business: { duration: 3000, showUser: true, logConsole: false },
    http: { duration: 3000, showUser: true, logConsole: true },
    console: { duration: 3000, showUser: false, logConsole: true }
  };

  // 全局网络错误状态
  private isNetworkError = false;
  private networkErrorTimer: number | null = null;

  public static getInstance(): ErrorManager {
    if (!ErrorManager.instance) {
      ErrorManager.instance = new ErrorManager();
    }
    return ErrorManager.instance;
  }

  /**
   * 处理错误（带去重）
   */
  handleError(
    error: any,
    type: 'network' | 'business' | 'http' | 'console' = 'http',
    context?: string
  ): void {
    const config = this.ERROR_CONFIG[type];
    const errorKey = this.generateErrorKey(error, type, context);

    // 检查是否在去重时间内
    const lastTime = this.errorCache.get(errorKey);
    const now = Date.now();
    
    if (lastTime && (now - lastTime) < config.duration) {
      return; // 跳过重复错误
    }

    // 记录错误时间
    this.errorCache.set(errorKey, now);

    // 显示用户错误
    if (config.showUser) {
      this.showUserError(error);
    }

    // 控制台日志
    if (config.logConsole && enableLog) {
      this.logConsoleError(error, type, context);
    }

    // 清理过期的错误记录
    this.cleanupExpiredErrors();
  }

  /**
   * 生成错误键
   */
  private generateErrorKey(error: any, type: string, context?: string): string {
    const message = error?.message || error?.toString() || 'unknown';
    const status = error?.response?.status || 'no-status';
    return `${type}-${status}-${message}-${context || 'default'}`;
  }

  /**
   * 显示用户错误
   */
  private showUserError(error: any): void {
    let message = '操作失败，请稍后重试';
    
    if (error?.response?.data?.message) {
      message = error.response.data.message;
    } else if (error?.message) {
      if (error.message.includes('Network Error') || error.message.includes('timeout')) {
        message = '网络连接失败，请检查网络设置';
        this.handleNetworkError();
      } else {
        message = error.message;
      }
    }

    ElMessage.error(message);
  }

  /**
   * 控制台错误日志
   */
  public logConsoleError(error: any, type: string, context?: string): void {
    const logKey = `${type}-${context || 'default'}`;
    const lastTime = this.consoleErrorCache.get(logKey);
    const now = Date.now();
    
    if (lastTime && (now - lastTime) < 3000) {
      return; // 避免控制台日志刷屏
    }
    
    this.consoleErrorCache.set(logKey, now);
    
    console.error(`${this.MODULE_PREFIX} [${type.toUpperCase()}]`, {
      error,
      context,
      timestamp: new Date().toISOString(),
      url: error?.config?.url,
      method: error?.config?.method,
      status: error?.response?.status,
      data: error?.response?.data
    });
  }

  /**
   * 处理网络错误
   */
  private handleNetworkError(): void {
    if (this.isNetworkError) return;
    
    this.isNetworkError = true;
    
    // 5秒后重置网络错误状态
    if (this.networkErrorTimer) {
      clearTimeout(this.networkErrorTimer);
    }
    
    this.networkErrorTimer = window.setTimeout(() => {
      this.isNetworkError = false;
      this.networkErrorTimer = null;
    }, 5000);
  }

  /**
   * 清理过期的错误记录
   */
  private cleanupExpiredErrors(): void {
    const now = Date.now();
    const maxAge = 60000; // 1分钟
    
    for (const [key, time] of this.errorCache.entries()) {
      if (now - time > maxAge) {
        this.errorCache.delete(key);
      }
    }
    
    for (const [key, time] of this.consoleErrorCache.entries()) {
      if (now - time > maxAge) {
        this.consoleErrorCache.delete(key);
      }
    }
  }
}

// ============= 拦截器工厂 =============

export function createRequestInterceptor(config: InterceptorConfig = {}) {
  const {
    addAuth = true,
    addFingerprint = false,
    addTimestamp = true,
    baseUrlHandler,
    enableErrorHandler = true,
    skipAuthRequests = true
  } = config;

  const errorManager = ErrorManager.getInstance();

  return {
    // 请求拦截器
    request: async (config: InternalAxiosRequestConfig) => {
      // 等待认证状态就绪
      if (addAuth) {
        const userStore = useUserStore();

        // 等待认证状态恢复完成
        let waitCount = 0;
        while (!userStore.isAuthReady && waitCount < 50) { // 最多等待5秒
          await new Promise(resolve => setTimeout(resolve, 100));
          waitCount++;
        }

        // 添加认证信息（从内存读取，不再直接读localStorage）
        const token = userStore.token;
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }

      // 添加浏览器指纹（仅在需要时）
      if (addFingerprint && config.headers) {
        const fingerprint = generateBrowserFingerprint();
        config.headers['X-Fingerprint'] = fingerprint;
      }

      // 添加时间戳
      if (addTimestamp && config.headers) {
        config.headers['X-Timestamp'] = Date.now().toString();
      }

      // 自定义 baseURL 处理
      if (baseUrlHandler) {
        config.baseURL = baseUrlHandler(config);
      }

      return config;
    },

    // 响应拦截器
    response: (response: AxiosResponse) => {
      // 保持原始响应格式，由API客户端负责解包
      return response;
    },

    // 错误拦截器（增强版，支持AppError）
    error: async (error: AxiosError) => {
      if (!enableErrorHandler) {
        return Promise.reject(error);
      }

      // 🔧 优先处理取消请求（CanceledError）：不重试、不报错、静默拒绝
      // 这种情况通常是请求去重机制主动取消的，不应触发错误提示和日志
      if (axios.isCancel(error) || error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
        const canceledError = buildAppError({
          code: ErrorCode.CANCELLED,
          message: '请求已取消',
          cause: error,
          feature: 'SDK',
          retryable: false  // 取消的请求不应重试
        });
        return Promise.reject(canceledError);
      }

      // 跳过认证请求的错误提示
      if (skipAuthRequests && error.config?.url?.includes('/auth/')) {
        return Promise.reject(error);
      }

      // 转换为AppError
      const httpStatus = error.response?.status;
      const bizCode = (error.response?.data as any)?.code ?? (error.response?.data as any)?.errorCode;
      const message = (error.response?.data as any)?.message ?? error.message;

      const errorCode = inferErrorCode({ httpStatus, bizCode, message });
      const appError = buildAppError({
        code: errorCode,
        httpStatus,
        message: message,
        cause: error,
        feature: 'SDK',
        correlationId: (error.config?.headers as any)?.[' X-Request-ID'] as string,
        // 根据错误码判断是否可重试
        retryable: errorCode === ErrorCode.NETWORK ||
                   errorCode === ErrorCode.TIMEOUT ||
                   errorCode === ErrorCode.RATE_LIMIT ||
                   errorCode === ErrorCode.SERVER
      });

      // 自动重试机制（仅对可重试错误）
      const cfg: any = error.config || {};
      cfg.__retryCount = cfg.__retryCount || 0;

      if (appError.retryable && cfg.__retryCount < 3) {
        cfg.__retryCount++;

        // 指数退避
        const baseDelay = 400;
        const jitter = Math.random() * 120;
        const delay = Math.min(4000, baseDelay * Math.pow(2, cfg.__retryCount)) + jitter;

        if (enableLog) {
          console.log(`[HTTP] 自动重试 (${cfg.__retryCount}/3)，${delay}ms后重试`, {
            url: cfg.url,
            errorCode: appError.code
          });
        }

        await new Promise(resolve => setTimeout(resolve, delay));
        return axios.request(cfg);
      }

      // 使用错误管理器处理错误（去重）
      // 检查是否为静默错误（不显示错误消息）
      const isSilentError = (error.config?.headers as any)?.[' X-Silent-Error'] === 'true' ||
                            (error.config?.headers as any)?.['X-Silent-Error'] === 'true';

      if (!isSilentError) {
        errorManager.handleError(appError, 'http', error.config?.url);
      }

      // 处理401未授权错误 - 自动登出
      if (httpStatus === 401 || appError.code === ErrorCode.UNAUTHORIZED) {
        // 动态导入避免循环依赖
        import('@/store/user').then(({ useUserStore }) => {
          const userStore = useUserStore();
          // 只有在用户已登录的情况下才执行登出
          if (userStore.isLoggedIn) {
            console.warn('[HTTP] 检测到令牌过期，自动登出');
            userStore.logout();
          }
        }).catch(err => {
          console.error('[HTTP] 自动登出失败:', err);
        });
      }

      // 抛出AppError而不是原始AxiosError
      return Promise.reject(appError);
    }
  };
}

// ============= API工厂 =============

export class ApiFactory {
  private client: AxiosInstance;
  private cache = new Map<string, { data: any; timestamp: number; ttl: number }>();
  private loadingStates = new Map<string, boolean>();
  private pendingRequests = new Map<string, AbortController>();

  constructor(client: AxiosInstance) {
    this.client = client;
  }

  /**
   * GET请求
   */
  async get<T = any>(url: string, config: ApiCallConfig = {}): Promise<AxiosResponse<T>> {
    return this.makeRequest<T>('get', url, undefined, config);
  }

  /**
   * POST请求
   */
  async post<T = any>(url: string, data?: any, config: ApiCallConfig = {}): Promise<AxiosResponse<T>> {
    return this.makeRequest<T>('post', url, data, config);
  }

  /**
   * PUT请求
   */
  async put<T = any>(url: string, data?: any, config: ApiCallConfig = {}): Promise<AxiosResponse<T>> {
    return this.makeRequest<T>('put', url, data, config);
  }

  /**
   * DELETE请求
   */
  async delete<T = any>(url: string, config: ApiCallConfig = {}): Promise<AxiosResponse<T>> {
    return this.makeRequest<T>('delete', url, undefined, config);
  }

  /**
   * PATCH请求
   */
  async patch<T = any>(url: string, data?: any, config: ApiCallConfig = {}): Promise<AxiosResponse<T>> {
    return this.makeRequest<T>('patch', url, data, config);
  }

  /**
   * 统一请求处理
   */
  private async makeRequest<T>(
    method: string,
    url: string,
    data?: any,
    config: ApiCallConfig = {}
  ): Promise<AxiosResponse<T>> {
    // 检查是否禁用去重
    const disableDedupe = config.transform?.disableDedupe || false;
    const requestKey = disableDedupe
      ? `${method.toUpperCase()}-${url}-${Date.now()}-${Math.random()}` // 禁用去重时生成唯一key
      : this.generateRequestKey(method, url, data, config.params);

    // 缓存处理
    if (config.cache?.enabled && method === 'get') {
      const cached = this.getFromCache(requestKey);
      if (cached) {
        return cached;
      }
    }

    // 加载状态处理
    if (config.loading?.enabled) {
      const loadingKey = config.loading.key || requestKey;
      this.setLoadingState(loadingKey, true);
    }

    try {
      // 取消之前的相同请求（仅在未禁用去重时）
      if (!disableDedupe) {
        this.cancelPendingRequest(requestKey);
      }

      // 创建新的取消控制器
      const abortController = new AbortController();
      this.pendingRequests.set(requestKey, abortController);

      console.log(`📡 [HTTP] 发送请求: ${method.toUpperCase()} ${url}${disableDedupe ? ' (去重已禁用)' : ''}`);

      // 发送请求
      const response = await this.client.request({
        method,
        url,
        data,
        ...config,
        signal: abortController.signal
      });

      // 缓存响应
      if (config.cache?.enabled && method === 'get') {
        this.setCache(requestKey, response.data, config.cache.ttl || 300000); // 默认5分钟
      }

      return response;
    } catch (error: any) {
      // 跳过请求取消错误的日志打印（避免无意义的错误日志）
      const isCancelledError = axios.isCancel(error) ||
                               error.code === 'ERR_CANCELED' ||
                               error.name === 'CanceledError' ||
                               error.message?.includes('请求已取消');

      if (!isCancelledError && enableLog) {
        console.log(`❌ [HTTP] 请求失败: ${method.toUpperCase()} ${url}`, error);
      }

      // 错误处理
      if (config.errorHandling?.customHandler) {
        config.errorHandling.customHandler(error);
      }
      throw error;
    } finally {
      // 清理状态
      this.pendingRequests.delete(requestKey);
      if (config.loading?.enabled) {
        const loadingKey = config.loading.key || requestKey;
        this.setLoadingState(loadingKey, false);
      }
    }
  }



  /**
   * 生成请求键
   */
  private generateRequestKey(method: string, url: string, data?: any, params?: any): string {
    let dataStr = '';

    // 特殊处理 FormData（兜底保护）
    if (data instanceof FormData) {
      const parts: string[] = [];
      data.forEach((value, key) => {
        if (value instanceof File) {
          // 文件：使用名称、大小、类型生成唯一标识
          parts.push(`${key}:file:${value.name}:${value.size}:${value.type}`);
        } else {
          // 普通字段：直接使用值
          parts.push(`${key}:${value}`);
        }
      });
      dataStr = parts.join('|');
    } else if (data) {
      // 普通数据：JSON 序列化
      dataStr = JSON.stringify(data);
    }

    const paramsStr = params ? JSON.stringify(params) : '';
    return `${method.toUpperCase()}-${url}-${dataStr}-${paramsStr}`;
  }

  /**
   * 缓存操作
   */
  private getFromCache(key: string): any {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < cached.ttl) {
      return cached.data;
    }
    this.cache.delete(key);
    return null;
  }

  private setCache(key: string, data: any, ttl: number): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  /**
   * 清除缓存
   */
  clearCache(key?: string): void {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }

  /**
   * 加载状态管理
   */
  private setLoadingState(key: string, loading: boolean): void {
    this.loadingStates.set(key, loading);
  }

  getLoadingState(key: string): boolean {
    return this.loadingStates.get(key) || false;
  }

  /**
   * 取消请求
   */
  cancelPendingRequest(key: string): void {
    const controller = this.pendingRequests.get(key);
    if (controller) {
      console.log(`🚫 [HTTP] 取消请求: ${key}`);
      controller.abort();
      this.pendingRequests.delete(key);
    }
  }
}

// ============= HTTP客户端创建 =============

/**
 * 创建HTTP客户端实例
 */
export function createHttpClient(config: InterceptorConfig = {}): AxiosInstance {
  const client = axios.create({
    baseURL: import.meta.env.VITE_BASE_URL || '',  // 从环境变量读取 baseURL
    timeout: 30000,
    withCredentials: true,  // 允许发送和接收 Cookie
    headers: {
      'Content-Type': 'application/json'
    }
  });

  // 应用拦截器
  const interceptors = createRequestInterceptor(config);

  client.interceptors.request.use(
    interceptors.request,
    (error) => Promise.reject(error)
  );

  client.interceptors.response.use(
    interceptors.response,
    interceptors.error
  );

  return client;
}

// ============= 默认实例 =============

// 创建默认HTTP客户端
const defaultClient = createHttpClient({
  addAuth: true,
  addFingerprint: true,  // 启用指纹发送，用于游客用户权限控制
  addTimestamp: true,
  enableErrorHandler: true,
  skipAuthRequests: true,
  standardResponseFormat: true
});

// 创建API工厂实例
const apiFactory = new ApiFactory(defaultClient);

// 创建错误管理器实例
export const errorManager = ErrorManager.getInstance();

// ============= 导出的API =============

/**
 * 统一的HTTP API
 */
export const http = {
  // 基础请求方法
  get: <T = any>(url: string, config?: ApiCallConfig) => apiFactory.get<T>(url, config),
  post: <T = any>(url: string, data?: any, config?: ApiCallConfig) => apiFactory.post<T>(url, data, config),
  put: <T = any>(url: string, data?: any, config?: ApiCallConfig) => apiFactory.put<T>(url, data, config),
  delete: <T = any>(url: string, config?: ApiCallConfig) => apiFactory.delete<T>(url, config),
  patch: <T = any>(url: string, data?: any, config?: ApiCallConfig) => apiFactory.patch<T>(url, data, config),

  // 工具方法
  clearCache: (key?: string) => apiFactory.clearCache(key),
  getLoadingState: (key: string) => apiFactory.getLoadingState(key),
  cancelRequest: (key: string) => apiFactory.cancelPendingRequest(key),

  // 创建新的客户端实例
  createClient: createHttpClient,

  // 获取默认客户端
  getDefaultClient: () => defaultClient
};

export default http;
